import { html } from 'lit-html';
import { Lit, Component, Prop, IComponent, BaseComponent, Expose } from '../base';
import '../Preview';
import { SwatchGroups, SwatchMaterial } from './types';
//import { IJHADesignerForm } from '../DesignerForm';
import { IJHADesignerPreview } from '../Preview';
import { Side, MeshType } from '../../types';
import { LayerOrder } from '../../engine/textures/layerdTexture';
import { FocusMode } from '../../engine';

export type SwatchMaterialLayer = {
  material: SwatchMaterial,
  layer: LayerOrder,
  side?: Side
}

const asyncSleep = (timeout: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, timeout);
  })
}

@Lit()
@Component('jha-designer', true)
export class JHADesigner extends BaseComponent {
  @Prop() scenerooturl?: string;
  @Prop() scenefile?: string;

  // @Prop() itemCode!: string;
  // @Prop() leftFacePlate!: string;
  // @Prop() leftShell!: string;
  // @Prop() leftArt!: string;
  // @Prop() leftfacePlate!: string;
  // @Prop() rightFacePlate!: string;
  // @Prop() rightShell!: string;
  // @Prop() rightArt!: string;
  // @Prop() rightfacePlate!: string;

  private swatchGroups?: SwatchGroups;
  private preview!: IJHADesignerPreview;
  //private designerForm?: IJHADesignerForm;
  private _onReady: Promise<void>;
  private _onReadyResolve!: () => void;

  constructor() {
    super();

    this._onReady = new Promise((resolve) => {
      this._onReadyResolve = resolve;
    })
  }

  @Expose()
  async setFocus(focus?: FocusMode) {
    await this._onReady;
    await this.preview.setFocus(focus);
  }

  @Expose()
  async start(material?: SwatchMaterial, internals?: SwatchMaterial, item_code?: string) {
    const result = await frappe.call<{ layers: SwatchMaterialLayer[], internals: SwatchMaterial}>({
      method: "jhaudio.designer.api.defaultMaterial",
      args: {
        item_code: item_code
      }
    });
    let matLayers: SwatchMaterialLayer[] = result.message.layers;
    if ( material ) {
      matLayers = [
        {
          material: material,
          layer: "Base"
        }
      ]
    }
    const internals_material = internals || result.message.internals as SwatchMaterial;

    await this._onReady;

    if ( this.preview ) {
      await this.preview.onReady();
      await this.updateAllLayers(matLayers, 'iem', false);
      await this.updateMaterial(internals_material, undefined, "Base", "inside");
    }

    return matLayers;
  }

  @Expose()
  async setLightIntensity(value: number) {
    if ( this.preview ) {
      this.preview.setLightIntensity(value);
    }
  }

  @Expose()
  async textureFromCanvas(name: string, options: any) {
    await this._onReady;

    if ( this.preview ) {
      return await this.preview.textureFromCanvas(name, options);
    }
  }

  onMount() {
    this.preview = this.root.getElementById("designer3d") as unknown as IJHADesignerPreview;
    this.preview.onReady()
      .then(() => this._onReadyResolve());
  }

  @Expose()
  async getCanvasElement() {
    await this._onReady;

    if ( this.preview ) {
      return this.preview.getCanvasElement()
    }
  }

  @Expose()
  async queueScreenshot(width: number, height: number) {
    await this._onReady;

    return await this.preview.queueScreenshot(width, height);
  }

  @Expose()
  async enableControls(enable: boolean = true, side?: Side) {
    await this._onReady;
    await this.preview.enableControls(enable, side);
  }

  @Expose()
  async updateDynamicTextures(meshType: MeshType = "iem", sides: Side[] = []): Promise<void> {
    await this._onReady;

    if ( this.preview ) {
      this.preview.drawLayers(meshType, sides);
    }
  }

  @Expose()
  async updateAllLayers(matLayers: SwatchMaterialLayer[], mesh: MeshType = "iem", setInternals: boolean = false ) {
    await this._onReady;

    if ( this.preview ) {
      await this.preview.beginTextureChange(mesh, [], async () => {
        if ( this.preview ) {

          for(let matLayer of matLayers) {
            const mat = matLayer.material;
            const side = matLayer.side;
            const layer = matLayer.layer;
            const left_base_color = mat.base_color;
            const tex_left_mask = mat.tex_left_mask;
            const tex_left_color = mat.tex_left_color;
            const tex_left_normal = mat.tex_left_normal;
            const tex_left_metallic = mat.tex_left_metallic;
            const tex_left_emissive = mat.tex_left_emissive || '/assets/jhaudio/3d/assets/textures/default-emissive.png';
      
            const right_base_color = mat.base_color;
            const tex_right_mask = mat.tex_right_mask || mat.tex_left_mask;
            const tex_right_color = mat.tex_right_color || mat.tex_left_color;
            const tex_right_normal = mat.tex_right_normal || mat.tex_left_normal;
            const tex_right_metallic = mat.tex_right_metallic || mat.tex_left_metallic;
            const tex_right_emissive = mat.tex_right_emissive || mat.tex_left_emissive;

            if ( side === "left" || !side ) {
              this.preview.setColorTex(mesh, 'left', tex_left_color, layer, left_base_color, tex_left_mask);
              this.preview.setNormalTex(mesh, 'left', tex_left_normal, layer, tex_left_mask);
              this.preview.setMetallicRoughnessTex(mesh, 'left', tex_left_metallic, layer, tex_left_mask);
              this.preview.setEmissiveTex(mesh, 'left', tex_left_emissive, layer, tex_left_mask);
            }
  
            if ( side === "right" || !side ) {
              this.preview.setColorTex(mesh, 'right', tex_right_color, layer, right_base_color, tex_right_mask);
              this.preview.setNormalTex(mesh, 'right', tex_right_normal, layer, tex_right_mask);
              this.preview.setMetallicRoughnessTex(mesh, 'right', tex_right_metallic, layer, tex_right_mask);
              this.preview.setEmissiveTex(mesh, 'right', tex_right_emissive, layer, tex_right_mask);
            }
          }
        }
      });

      if ( setInternals ) {
        const result = await frappe.call<{ material: SwatchMaterial, internals: SwatchMaterial}>({
          method: "jhaudio.designer.api.defaultMaterial",
        });  
        const inside_mat = result.message.internals as SwatchMaterial;
        await this.updateMaterial(inside_mat, undefined, "Base", "inside");
  
      }
  
    }
  }

  @Expose()
  async updateMaterial(mat: SwatchMaterial, side?: Side, layer: LayerOrder = "Base", mesh: MeshType = "iem") {
    await this._onReady;

    if ( this.preview ) {
      const left_base_color = mat.base_color;
      const tex_left_mask = mat.tex_left_mask;
      const tex_left_color = mat.tex_left_color;
      const tex_left_normal = mat.tex_left_normal;
      const tex_left_metallic = mat.tex_left_metallic;
      const tex_left_emissive = mat.tex_left_emissive || '/assets/jhaudio/3d/assets/textures/default-emissive.png';

      const right_base_color = mat.base_color;
      const tex_right_mask = mat.tex_right_mask || mat.tex_left_mask;
      const tex_right_color = mat.tex_right_color || mat.tex_left_color;
      const tex_right_normal = mat.tex_right_normal || mat.tex_left_normal;
      const tex_right_metallic = mat.tex_right_metallic || mat.tex_left_metallic;
      const tex_right_emissive = mat.tex_right_emissive || mat.tex_left_emissive;

      await this.preview.beginTextureChange(mesh, side?[side]:[], async () => {
        if ( this.preview ) {
          if ( side === "left" || !side ) {
            this.preview.setColorTex(mesh, 'left', tex_left_color, layer, left_base_color, tex_left_mask);
            this.preview.setNormalTex(mesh, 'left', tex_left_normal, layer, tex_left_mask);
            this.preview.setMetallicRoughnessTex(mesh, 'left', tex_left_metallic, layer, tex_left_mask);
            this.preview.setEmissiveTex(mesh, 'left', tex_left_emissive, layer, tex_left_mask);
          }

          if ( side === "right" || !side ) {
            this.preview.setColorTex(mesh, 'right', tex_right_color, layer, right_base_color, tex_right_mask);
            this.preview.setNormalTex(mesh, 'right', tex_right_normal, layer, tex_right_mask);
            this.preview.setMetallicRoughnessTex(mesh, 'right', tex_right_metallic, layer, tex_right_mask);
            this.preview.setEmissiveTex(mesh, 'right', tex_right_emissive, layer, tex_right_mask);
          }
        }
      })
    }
  }

  render() {
    return html`
      <style>
        :host(jha-designer) {
          position: relative;
          display: flex;
          flex: 1 1 auto;
          align-items: stretch;
        }

        /**
        * engine canvas setup
        */
        #designer3d {
          position: relative;
          flex: 1 1 auto;
          display: flex;
          align-items: stretch;
        }

      </style>
      <jha-designer-preview id="designer3d" focus="left" sceneRootUrl="${this.scenerooturl}" sceneFile="${this.scenefile}"></jha-designer-preview>
    `;
  }
}